import { useState, useEffect, useRef } from 'react';
import { useRouteMatch } from 'react-router';
import { useCustomerTypePopup } from '../StartPage/useCustomerTypePopup';

const extractCartItemInfo = (item, url) => {
  return {
    ProductID: item?.id,
    SKU: item?.articleNumber,
    ProductName: item?.product?.name,
    Quantity: item?.quantity,
    ItemPrice: item?.previousUnitPrice?.incVat,
    RowTotal: item?.total?.incVat,
    ProductURL: `${url}/${item?.product?.primaryRoute?.id}`,
    ImageURL: item?.product?.images?.[0]?.url,
  };
};

export const KlaviyoTrackViewedItem = ({
  productId,
  product,
  selectedChannel,
}) => {
  let url = selectedChannel?.url;
  useEffect(() => {
    if (typeof window !== 'undefined' && product) {
      if (!window._learnq) window._learnq = [];

      const productCategories = (product.categories || []).map(
        (category) => category.name
      );

      const item = {
        ProductName: `${product.name}`,
        ProductID: `${product.id}`,
        SKU: `${product.articleNumber}`,
        Categories: productCategories,
        ImageURL: `${product.images?.[0]?.url}`,
        URL: `${url}/${product.primaryRoute?.id}`,
        Brand: '',
        Price: product.price.incVat,
      };

      window._learnq.push(['track', 'Viewed Product', item]);

      window._learnq.push([
        'trackViewedItem',
        {
          Title: item.ProductName,
          ItemId: item.ProductID,
          Categories: item.Categories,
          ImageUrl: item.ImageURL,
          Url: item.URL,
          Metadata: {
            Brand: item.Brand,
            Price: item.Price,
          },
        },
      ]);
    }
  }, [productId, product, url]);
  return null;
};

export const KlaviyoTrackAddToCartItems = ({ cart, selectedChannel }) => {
  const [previousCartItems, setPreviousCartItems] = useState();
  const findAddedProduct = (newCartItems) => {
    let increasedQuantity;

    const addedProduct = newCartItems.find((newItem) => {
      const previousItem = previousCartItems.find(
        (_prevItem) => _prevItem.id === newItem.id
      );
      if (previousItem == null) {
        return true;
      } else {
        if (previousItem.quantity < newItem.quantity) {
          increasedQuantity = newItem.quantity - previousItem.quantity;
          return true;
        }
      }
      return false;
    });

    return [addedProduct, increasedQuantity];
  };

  const reportAddedToCart = (cart, addedItem, increasedQuantity) => {
    if (typeof window !== 'undefined' && cart?.items?.length) {
      if (!window._learnq) window._learnq = [];
      let url = selectedChannel?.url;

      const itemNames = cart.items.map((item) => item.product?.name);
      const cartItems = cart.items.map((product) =>
        extractCartItemInfo(product, url)
      );

      const payload = {
        $value: cart.productTotal.incVat,
        AddedItemProductName: addedItem.product?.name,
        AddedItemProductID: addedItem.id,
        AddedItemSKU: addedItem.articleNumber,
        AddedItemImageURL: addedItem.product?.images?.[0]?.url,
        AddedItemURL: `${url}/${addedItem.product?.primaryRoute?.id}`,
        AddedItemPrice: addedItem.previousUnitPrice?.incVat,
        AddedItemQuantity: increasedQuantity || addedItem.quantity,
        ItemNames: itemNames,
        CheckoutURL: cart.externalCheckoutUrl,
        Items: cartItems,
      };

      console.log('Klaviyo cart info !', payload);

      window._learnq.push(['track', 'Added to Cart', payload]);
    }
  };

  useEffect(() => {
    let addedItem, increasedQuantity;
    if (previousCartItems != null && cart?.items)
      [addedItem, increasedQuantity] = findAddedProduct(cart.items);

    setPreviousCartItems(cart?.items);

    if (addedItem) reportAddedToCart(cart, addedItem, increasedQuantity);
  }, [cart]);

  return null;
};

export const klaviyoTrackStartedCheckout = (cart, selectedChannel) => {
  if (typeof window !== 'undefined') {
    if (!window._learnq) window._learnq = [];
    let url = selectedChannel?.url;
    const timestamp = new Date().getTime();

    const itemNames = cart.items.map((item) => item.product?.name);
    const cartItems = cart.items.map((product) =>
      extractCartItemInfo(product, url)
    );

    console.log(cartItems);

    const startedCheckout = {
      $event_id: `${cart.id}_${timestamp}`,
      $value: cart.productTotal.incVat,
      ItemNames: itemNames,
      CheckoutURL: cart.externalCheckoutUrl,
      Items: cartItems,
    };

    window._learnq.push(['track', 'Started Checkout', startedCheckout]);
  }

  return;
};

const KlaviyoProvider = ({ selectedChannel }) => {
  const allChannels = {
    'mrplantshop.se': 'SRMEKH',
  };

  const domain = selectedChannel.url
    ?.replace('https://', '')
    .replace('www.', '');
  const apiKey = allChannels[domain];

  //ON THE START PAGE, WAIT UNTIL THE CUSTOMER TYPE POPUP IS ANSWERED SO THE KLAVIYO FORMS NEVER COVER IT
  const isStartPage = !!useRouteMatch({ path: '/', exact: true });
  const { shouldShow: customerTypePopupPending } = useCustomerTypePopup();
  const waitForCustomerType = isStartPage && customerTypePopupPending;
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!apiKey) {
      console.log(
        `No channel in KlaviyoProvider for the selected domain. Looking for: ${domain}`
      );
      console.log(selectedChannel);
      return;
    }
    if (waitForCustomerType || scriptLoaded.current) return;
    scriptLoaded.current = true;

    const script = document.createElement('script');
    script.type = 'application/javascript';
    script.async = true;
    script.defer = true;
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${apiKey}`;

    document.body.appendChild(script);
  }, [apiKey, domain, selectedChannel, waitForCustomerType]);
  return null;
};

export default KlaviyoProvider;
